import moment from "moment";

import { LanguageOptions } from "@/Administration/Models/UI/Enums/Administration";
import { CallCenterUserState } from "@/Common/Api/CallCenter/Responses/CallCenterUserState";
import { CallCenterUserCredentialsWithoutSecret } from "@/Common/Api/CallCenter/Responses/LoginToCallCenterResponse";
import { Client } from "@/Common/Api/Client";
import { ErrorMessage } from "@/Common/Api/Dto/ErrorMessage";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { LicenseDto } from "@/Common/Api/Labs/Dto/Responses/LicenseDto";
import { TimerComponentModel } from "@/Common/Components/Inputs/TimerComponent";
import { Popup } from "@/Common/Components/Modals/Popup";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { CallCenterUserConnectionState } from "@/Common/Models/CallCenter/CallCenterUserConnectionState";
import { ClientConfiguration } from "@/Common/Models/ClientConfiguration";
import { EmployeeTitle } from "@/Common/Models/Employee/Enums/EmployeeTitle";
import { EmployeeType } from "@/Common/Models/Employee/Enums/EmployeeType";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { Guid } from "@/Common/Models/Guid";
import { Locale } from "@/Common/Models/Localization/Locale";
import { LocaleNumber } from "@/Common/Models/Localization/LocaleNumber";
import { LocalizedTextDto } from "@/Common/Models/LocalizedTextDto";
import { AuthenticatorCodeDeliveryMethod } from "@/Common/Models/MultifactorAuthentication/AuthenticatorCodeDeliveryMethod";
import { MyleTabKey } from "@/Common/Models/MyleMenu/enums/MyleTabKey";
import { Patient } from "@/Common/Models/Patient/Patient";
import { PatientExternalIdentifier } from "@/Common/Models/Patient/PatientExternalIdentifier";
import { Person } from "@/Common/Models/Person/Person";
import { TimeSpan } from "@/Common/Models/TimeSpan";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { PopupModel } from "@/Common/Models/UI/PopupModel";
import { UnverifiedEmail } from "@/Common/Models/UnverifiedEmail";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { DocumentClass } from "@/Common/Resources/Lookups/Enums/DocumentClasses";
import { Dispatcher } from "@/Common/Stores/Actions/ActionDispatcher";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { LoginAction } from "@/Common/Stores/Actions/LoginAction";
import { ServerEvent } from "@/Common/Stores/Actions/ServerEvent";
import { BaseStore } from "@/Common/Stores/BaseStore";
import { CallCenterRealTimeNotificationHandler } from "@/Common/Stores/Handlers/CallCenterRealTimeNotificationHandler";
import { RequestMapper, ServerResponseMapper } from "@/Common/Stores/Handlers/Server/RequestMapper";
import { SessionResponseHandler } from "@/Common/Stores/Handlers/Server/SessionResponseHandler";
import { LoginWorkflow } from "@/Common/Workflow/LoginWorkflow/LoginWorkflow";
import { LoginWorkflowStepEnum } from "@/Common/Workflow/LoginWorkflow/LoginWorkflowStepEnum";
import { RvqAuthenticationManager } from "@/Patient/Components/MedicalItems/Immunization/RvqAuthenticationManager";
import { APSSPatientDTO } from "@/Patient/Models/Forms/Apss/APSSPatientDTO";
import { PrescriptionRenewalItemResponse } from "@/PrescriptionRenewals/Api/Dto/PrescriptionRenewalItemResponse";
import { fetchPrescriptionsRenewalCounters } from "@/PrescriptionRenewals/Api/PrescriptionRenewalsClient";
import { PrescriptionRenewalStatus } from "@/PrescriptionRenewals/Common/PrescriptionRenewalStatus";
import { PrescriptionRenewal } from "@/PrescriptionRenewals/Models/PrescriptionRenewal";
import { ILoginErrorMessage, ILoginModel } from "../../Common/Models/UI/Login/ILoginModel";
import { ClinicActiveProvince } from "../Models/Utils/ClinicActiveProvince";
import { SessionHandler } from "./Handlers/Local/SessionHandler";

let defaultInstance: SessionStore;

class SessionStore extends BaseStore {
    private mapper: RequestMapper;
    private responseMapper: ServerResponseMapper;
    private handler: SessionHandler;
    private responseHandler: SessionResponseHandler;
    private timeUntilSessionSlideMinutes: number;
    private timeUntilSessionInactiveMinutes: number;
    private timeDurationWarningPopupMinutes: number;
    private timeGracePeriodMinutes: number;
    private isUserActivityDetected: boolean;
    private lastActivity: Date;
    private cachedCurrentEmployee: EmployeeUIModel; // !! CachedCurrentEmployee stays private !!  -->  use GetCurrentEmployee()
    private currentId: number = 0;
    private lockedData: { [index: string]: any } = {};
    private callCenterNotificationHandler: CallCenterRealTimeNotificationHandler;
    private callCenterUserState: CallCenterUserState;

    public readonly debugSessionTicks: boolean = false;
    public versionNumber: string = process.env.REACT_APP_VERSION; // update in Directory.Build.props and Myle.Web/Properties/AssemblyInfo.cs
    public action: LoginAction;
    public helper: MyleHelper;
    public userPermissions: string[];
    public myleEmployeeType: EmployeeType; // session mgmt variables
    public tokenExpiration: Date;

    // application state variables
    public loading: boolean;

    // user specific variables
    public clientLoginModel: ILoginModel;
    public myleEmployeeTitle: EmployeeTitle;
    public userId: string;
    public isTempPassword: boolean;

    public loginWorkflow: LoginWorkflow = null;
    public userWalkmeActivationDate?: Date;

    /**
     * The current language.
     */
    public locale: Locale = Locale.en;

    /**
     * @deprecated Use locale instead.
     */
    public get userLanguageCode(): string {
        return this.locale == Locale.es
            ? "es-MX"
            : this.locale;
    }

    /**
     * The MSA Id.
     */
    public get msaId(): string {
        return window.msa_id;
    }

    /**
     * Whether the user has a MSA Id.
     */
    public get isMsaActive(): boolean {
        return !IsNullOrUndefined(this.msaId);
    }

    public get currentCallCenterUserState(): CallCenterUserState {
        // only report state if they're logged in as we may have received state during the login process
        if (sessionStore.CallCenter_UserConnectionState === CallCenterUserConnectionState.Connected) {
            return this.callCenterUserState;
        }
        return null;
    }

    public individualContactId: string;
    public lastFocusedClickableTextController: string;
    public lastValidUserName: string;
    public acceptedAgreement: boolean;
    public hasEmail: boolean;
    /*
     * Contains information about the user's current email that we have on file that is not verified.
     * On login, this email will be shown in the popup and will force the user to verify their email before completing the login.
     */
    public unverifiedEmail: UnverifiedEmail;
    /*
     * The last time the user was first prompted to enter an email if they don't have one.
     * If the date is not null, the final prompt will force the user to enter their email before continuing to login.
     */
    public lastRecoveryEmailPromptDate: Date | null;
    /*
     * If the recovery email is required on login for the specific user.
     */
    public isRecoveryEmailRequired: boolean;
    public lastVerifiedEmail: string;
    public hasPinSet: boolean;
    public userCreationDate?: Date;
    public signOnPrintAccess: boolean;
    public faxAccess: boolean;
    public useMetricSystemAsDefault: boolean;
    public isScheduledVisitChecked: boolean; // Medfar Billing Stuff (Legacy FMP)
    public has500patient: boolean; // Medfar Billing Stuff (Legacy FMP)
    public lastSelectedNoteExternalCategory: string; // used by cemtl to remember the last note class selected when note is approved
    public DSQPin: string;
    public DSQPrescriptionDisabled: boolean;
    public DSQPrescriptionError: number;
    public lockedSupervisorContactId: string;
    public pharmaNetUsername: string;
    public pharmaNetPassword: string;
    public AuthenticatorCodeDeliveryMethod: AuthenticatorCodeDeliveryMethod;

    // client (clinic) specific settings
    public clientConfiguration: ClientConfiguration;
    public cachedPrivateBillSiteId: Guid;
    public cachedPrivateBillCostCenterId: string;
    public timeoutReferenceRefreshCounters: number;
    public refreshCounterLastQuery: Date;
    public privateBillProviderContactFacetId: string;
    public rememberPrivateBillProvider: boolean;
    public popups: Popup[];

    /*
     * The current email of the user used during the login workflow.
     * Only entered during the login workflow (not require email workflow)
     */
    public currentlyAddedEmail: string;
    public currentlyAddedContactPointId: string;

    public isRequiredEmailFlowEnded: boolean = false;

    /**
     * Manages authentication with the Quebec Vaccination Registry (RVQ).
     */
    public readonly rvq = new RvqAuthenticationManager();

    // Timer
    public timerComponentModel: TimerComponentModel = null;
    public timerCurrentValue: TimeSpan;
    public activeMYLEMenuTab: MyleTabKey;

    public hidePatientLinkUntilLogoff: boolean = false;

    // The currently opened files (patient or relative).
    public selectedFile?: Person = null;
    public selectedFilesHistory: Person[] = [];

    public goLiveDate?: Date;

    public customPasswordRuleMessage?: LocalizedTextDto;
    public customAdminPasswordRuleMessage?: LocalizedTextDto;
    public customPasswordValidationRegex?: string;

    public toggleMedicalSummarySection: boolean = false;

    public prefillMessageSelectedClinicians: Person[];
    public prefillTaskSelectedClinicians: Person[];
    public prefillRecallSelectedClinicians: Person[];

    public CallCenter_SessionId: Guid;
    public CallCenter_Credentials: CallCenterUserCredentialsWithoutSecret;
    public CallCenter_UserConnectionState: CallCenterUserConnectionState;
    public CallCenter_LastLoginError: ErrorMessage[];
    public CallCenter_AutoOpenPatientOnSingleMatch: boolean = false;
    public CallCenter_ShowLoginAccountPopup: boolean = false;

    public ClinicActiveProvinces: ClinicActiveProvince[];

    // required multifactorAuthentication login work flow
    public IsMultifactorAuthenticationRequired = true;
    public MultifactorAuthenticationSetupPromptDates: Date[];
    public IsMultifactorAuthenticationEnabled: boolean = false;

    constructor() {
        super();
        let self = this;

        this.clientLoginModel = Immutable.fromJS({
            UserName: "",
            Password: "",
            ErrorMessage: null,
            ValidatingCredentials: false,
            LoggedIn: false,
            SignInRequired: false,
            OpenRequestResetPasswordControl: false,
            AddEmailInProcess: false,
            RequestResetPasswordInProgress: false,
            RequestResetPasswordSuccessful: false,
            LockedUserName: false,
            ShowPrivacyPolicy: false,
            AcceptAgreementChecked: false,
            AcceptingAgreementInProgress: false,
            IsNewAdminUser: false,
            IsCacheLoaded: false,
            ShowAuthenticatorCodeForm: false
        });
        let e: ILoginErrorMessage = { UseLocalResource: false, Message: "" };

        this.clientLoginModel = this.clientLoginModel.set("ErrorMessage", e);
        this.action = new LoginAction();
        this.helper = new MyleHelper();
        this.mapper = new RequestMapper();
        this.handler = new SessionHandler();
        this.mapper.AddInstance(this.handler);
        this.responseMapper = new ServerResponseMapper();
        this.responseHandler = new SessionResponseHandler(this.action);
        this.responseMapper.AddInstance(this.responseHandler);
        this.loading = false;
        this.popups = [];

        this.DSQPin = "";
        this.DSQPrescriptionDisabled = false;
        this.DSQPrescriptionError = 0;
        this.isScheduledVisitChecked = null;

        this.ServerDispatchToken = ServerResponseDispatcher.register((response: ServerEvent) => {
            self.responseMapper.Map(response, self);
        });
        this.ClientDispatchToken = LocalEventDispatcher.register((request: LocalEvent) => {
            self.updateActivity(); self.mapper.Map(request, self);
        });
    }

    public static createSingleton(): SessionStore {
        return this.createStoreSingleton(defaultInstance, () => defaultInstance = new SessionStore());
    }

    public static createSingletonFrom(store: SessionStore, copy: boolean = true): SessionStore {
        return copy
            ? Object.assign(this.createSingleton(), store)
            : this.createStoreSingleton(defaultInstance, () => defaultInstance = store);
    }

    /**
     * Set the locale given a legacy language code.
     * @param language The language code.
     * @deprecated Set locale instead.
     */
    public setLanguageCode(language: string): void {
        switch ((language || "").toLowerCase()) {
            case "es":
            case "es-mx":
                this.locale = Locale.es;
                break;

            case "fr":
                this.locale = Locale.fr;
                break;

            default:
                this.locale = Locale.en;
                break;
        }
    }

    public continueWithLogin(employeeType: EmployeeType): void {
        const isDoctor = employeeType === EmployeeType.Practician;
        const hasPrescriptionRenewalsAccess = this.userPermissions.some(x => x === "PrescriptionRenewalTabPermission") && cacheStore.ClinicSettings?.EnablePrescriptionRenewal;
        cacheStore.RequestApplicationStringAsync(
            this.locale,
            () => {
                this.action.loadCache(isDoctor || hasPrescriptionRenewalsAccess);
                this.toggleNavigationPrompt(true);

                if (this.debugSessionTicks) {
                    console.log("Session Timer Started");
                }
            }
        );
    }

    /**
     * enables navigation prompt when closing window or leaving site
     */
    public toggleNavigationPrompt(show: boolean): void {
        // enable navigation prompt when closing window or leaving site

        if (show) {
            window.onbeforeunload = () => true;
        }
        else {
            window.onbeforeunload = null;
        }
    }

    public isAuthenticated(): boolean {
        return this.clientLoginModel.get("LoggedIn") === true;
    }

    /**
     * Get whether the current user has access to the admin tab.
     */
    public isAdmin(): boolean {
        return this.hasPermission(EnumMyleLoginPermission.AdminTabPermission);
    }

    /**
     * Get whether the current has a permission.
     * @param id The permission to check.
     */
    public hasPermission(id: EnumMyleLoginPermission): boolean {
        const userHasPermission = this.userPermissions.some(x => x === EnumMyleLoginPermission[id]);

        switch (id) {
            case EnumMyleLoginPermission.CanCreateAndEditPatientPermission:
                return userHasPermission && !!cacheStore.ClinicSettings?.EnableCanCreatePatientPermission;

            case EnumMyleLoginPermission.PrescriptionRenewalTabPermission:
                return userHasPermission && cacheStore.ClinicSettings?.EnablePrescriptionRenewal;

            case EnumMyleLoginPermission.OrdersTabPermission:
                return userHasPermission && cacheStore.ClinicSettings?.EnablePharmacyOrders;

            default:
                return userHasPermission;
        }
    }

    /**
     * Get whether the current has permission to access a Myle app path.
     * @param id The request path relative to the server root.
     */
    public hasPermissionForPath(path: string): boolean {
        path = path.replaceAll("/", "");

        switch (path) {
            case "":
            case "myle":
                return true;

            case "calendar":
                return this.hasPermission(EnumMyleLoginPermission.CalendarTabPermission);

            case "patients":
                return this.hasPermission(EnumMyleLoginPermission.PatientTabPermission);

            case "communications":
            case "communications_optimizedv2":
                return this.hasPermission(EnumMyleLoginPermission.CommunicationsTabPermission);

            case "documents":
                return this.hasPermission(EnumMyleLoginPermission.DocumentTabPermission);

            case "billing":
                return cacheStore.ClinicSettings.EnableBillingReconciliationReport ?
                    this.hasPermission(EnumMyleLoginPermission.BillingTabPermissionCurrentUserOnly) || this.hasPermission(EnumMyleLoginPermission.BillingTabPermission) ||
                    this.hasPermission(EnumMyleLoginPermission.PrivateBillingPermission) || this.hasPermission(EnumMyleLoginPermission.PublicBillingPermission) :
                    this.hasPermission(EnumMyleLoginPermission.BillingTabPermissionCurrentUserOnly) || this.hasPermission(EnumMyleLoginPermission.BillingTabPermission);

            case "results":
                return this.hasPermission(EnumMyleLoginPermission.LabsTabPermission);

            case "administration":
                return this.hasPermission(EnumMyleLoginPermission.AdminTabPermission);

            case "affectation":
            case "scheduling":
                return this.hasPermission(EnumMyleLoginPermission.AffectationTabPermission);

            case "lists":
                return this.hasPermission(EnumMyleLoginPermission.ListsTabPermission);

            case "portfolio":
                return this.hasPermission(EnumMyleLoginPermission.PortfolioTabPermission);

            case "reporting":
                return this.hasPermission(EnumMyleLoginPermission.ReportingTabPermission);

            case "settings":
                return this.hasPermission(EnumMyleLoginPermission.SettingsTabPermission);

            case "prescriptionrenewals":
                return this.hasPermission(EnumMyleLoginPermission.PrescriptionRenewalTabPermission);

            case "orders":
                return this.hasPermission(EnumMyleLoginPermission.OrdersTabPermission);

            default:
                return false;
        }
    }

    public isSignInRequired(): boolean {
        return this.clientLoginModel.get("SignInRequired") === true;
    }

    /**
     * Show the default global overlay if it isn't already shown.
     * @param hasBackground Whether to show a colored background.
     *
     * @deprecated Use overlayStore.showOverlay instead.
     */
    public showGlobalOverlay(hasBackground?: boolean): void {
        overlayStore.showOverlay("global-overlay", { transparent: hasBackground === false });
    }

    /**
     * Hide the default global overlay if it's shown.
     * @deprecated Use overlayStore.hideOverlay instead.
     */
    public hideGlobalOverlay(): void {
        overlayStore.hideOverlay("global-overlay");
    }

    public getCurrentEmployee(): EmployeeUIModel | null {
        if (this.cachedCurrentEmployee != null) {
            return this.cachedCurrentEmployee;
        }

        for (let composite of cacheStore.EmployeeCache) {
            let employee = EmployeeUIModel.fromCompositeObject(composite);
            if (employee != null && employee.Model.Contact_id == this.individualContactId) {
                this.cachedCurrentEmployee = employee;
                return employee;
            }
        }
        return null;
    }

    public getCurrentEmployeeType(): EmployeeType {
        return this.getCurrentEmployee().Type;
    }

    /**
     * Get the default health card ID translation for the employee's active site.
     */
    public getDefaultHealthCardLabel(): string {
        let activeSite = this.getCurrentEmployee().getActiveSite();
        let address = activeSite.Model.Adresses && activeSite.Model.Adresses.length > 0
            ? activeSite.Model.Adresses[0]
            : null;

        return cacheStore.GetHealthCardLabel(address?.CountryLookupId, address?.StateLookupId);
    }

    public getCurrentEmployeeActiveSiteStateLookUpId(): string {
        return this
            .getCurrentEmployee()
            .getActiveSite()
            ?.getPreferredAddress()
            ?.StateLookupId;
    }

    public rebindCurrentEmployee(employee: EmployeeUIModel): EmployeeUIModel {
        this.cachedCurrentEmployee = employee;
        return employee;
    }

    /**
     * Forget the cached current employee, so it's re-read from the underlying data on the next request.
     */
    public clearCurrentEmployeeCache(): void {
        this.cachedCurrentEmployee = null;
    }

    public updateCurrentEmployee(update: (employee: Employee) => void): void {
        for (const employee of cacheStore.EmployeeCache) {
            if (employee.Contact_id == this.individualContactId)
                update(employee);
        }

        this.clearCurrentEmployeeCache();
    }

    public getCurrentEmployeeDefaultSpecializationLookupId(): string {
        let result = null;
        const currentEmployeeSpecializations = this.getCurrentEmployee().getServiceSpecializations();
        const currentUserDefaultSpecializationLookupId = cacheStore.CurrentUserDefaultSpecializationLookupId;

        if (!!currentUserDefaultSpecializationLookupId && currentUserDefaultSpecializationLookupId != GuidHelper.Empty()) {
            if (currentEmployeeSpecializations?.length === 1) {
                const currentEmployeeSpecialization = currentEmployeeSpecializations[0];
                const isAdHoc = ["Ad-Hoc", "Ad-HocSpe", ""].includes(currentEmployeeSpecialization.LookupCode);

                if (currentEmployeeSpecialization.LookupID === currentUserDefaultSpecializationLookupId && isAdHoc) {
                    return result;
                }
            }
            else if (!currentEmployeeSpecializations.any()) {
                return result;
            }

            result = cacheStore.CurrentUserDefaultSpecializationLookupId;
        }
        else {
            if (!(currentEmployeeSpecializations == null) && currentEmployeeSpecializations.length > 0) {
                result = currentEmployeeSpecializations[0].LookupID;
            }
        }

        return result;
    }

    public hasRAMQ(): boolean {
        let result = true;
        if (typeof this.clientConfiguration !== "undefined" && this.clientConfiguration !== null && !(this.clientConfiguration.HasMedfarRAMQ)) {
            result = this.clientConfiguration.HasMedfarRAMQ;
        }

        return result;
    }

    public hasDsqNIP(): boolean {
        if (typeof this.DSQPin === "undefined" || this.DSQPin === null) {
            return false;
        }
        return this.DSQPin.length > 0;
    }

    public hasRightsForAction(requireTypes: EmployeeType[] = null, requireTitles: EmployeeTitle[] = null, disallowTitles: EmployeeTitle[] = null): boolean {
        if (requireTypes?.any() && !requireTypes.contains(this.myleEmployeeType))
            return false;

        if (requireTitles?.any() && !requireTitles.contains(this.myleEmployeeTitle))
            return false;

        if (disallowTitles?.contains(this.myleEmployeeTitle))
            return false;

        return true;
    }

    public canApproveDocument(): boolean {
        return this.hasRightsForAction(
            [EmployeeType.Practician],
            null,
            [
                EmployeeTitle.Student,
                EmployeeTitle.Extern,
                EmployeeTitle.MedicalOfficeAssistant,
                EmployeeTitle.Unknown,
                EmployeeTitle.NONE
            ]
        );
    }

    /**
     * Show a generic popup message.
     * @param content The messages or options to display.
     */
    public showPopup(content: string | ErrorMessage | (string | ErrorMessage)[] | Partial<PopupModel>): void {
        // get model
        let maybeErrorMessage = content && (<ErrorMessage>content).Message;

        let model = (typeof (content) == "string" || maybeErrorMessage || Array.isArray(content))
            ? new PopupModel({ content: content as any })
            : new PopupModel(content as Partial<PopupModel>);

        // raise popup
        let op = new LocalEvent(LocalOperations.MainComponentViewOperations.DisplayCommonPopup);
        op.AddItem("model", model);
        MainComponentStateDispatcher.dispatch(op);
    }

    public getUserLanguageCodeFRorEN(): string {
        return this.locale == Locale.fr
            ? "fr"
            : "en";
    }

    /**
     * Format error messages from the backend for display in the UI.
     * @param messages The content to format.
     */
    public formatErrorsForDisplay(messages: string | ErrorMessage | (string | ErrorMessage)[]): string[] {
        if (!messages)
            return [];

        if (!Array.isArray(messages))
            messages = [messages] as (string | ErrorMessage)[];

        return (messages as any[])
            .filter(message => message)
            .map(message => {
                const error = <ErrorMessage>message;
                let text = error.Message || error.Code || message;
                if (message.VocabularyKey)
                    text = cacheStore.GetString(message.VocabularyKey) || text;
                return text;
            });
    }

    public getDateFormatFromLanguage(): string {
        switch (this.locale) {
            case Locale.fr:
                return "DD-MM-YYYY";

            case Locale.en:
                return "DD/MM/YYYY";

            case Locale.es:
                return "DD-MM-YYYY";

            default:
                return "DD-MM-YYYY";
        }
    }

    public getUserLanguageCodeFull(): string {
        switch (this.locale) {
            case Locale.fr:
                return "fr-CA";

            case Locale.es:
                return "es-MX";

            default:
                return "en-CA";
        }
    }

    public getLanguageStringFromEnum(language: LanguageOptions): string {
        if (language == LanguageOptions.French)
            return "fr";
        else if (language == LanguageOptions.Spanish)
            return "es-MX";
        else if (language == LanguageOptions.English)
            return "en";
        else
            return sessionStore.locale;
    }

    public async getAPSSToken(patient: APSSPatientDTO = null, forceHistoricalId: string = null): Promise<{ token: string, patientDtoFilePath: string }> {
        const response = await Client.postAsync<{ Cookie: string, PatientDtoBase64FilePath: string }>(
            ServerOperations.Server.Myle_SetApssCookie,
            {
                UserDisplayName: sessionStore.getCurrentEmployee().nameWithPrefix(),
                ClinicHistoricId: forceHistoricalId ?? sessionStore.getCurrentEmployee().getActiveSite().Model.ClinicHistoricalIds,
                Patient: patient
            }
        );

        return {
            token: response.data?.Cookie,
            patientDtoFilePath: response.data?.PatientDtoBase64FilePath
        };
    }

    public hasAPSSAccess(): boolean {
        return !IsNullOrWhiteSpace(sessionStore.getCurrentEmployee().Model.FirstLicenceNumber)
            && (
                EmployeeTitleHelper.isDoctor(sessionStore.myleEmployeeTitle)
                || EmployeeTitleHelper.isProfessional(sessionStore.myleEmployeeTitle)
            );
    }

    public getAPSSEmployee(): Person {
        if (this.hasAPSSAccess)
            return Person.FromEmployee(sessionStore.getCurrentEmployee().Model);
        else
            return null;
    }

    public newId(): number {
        this.currentId += 1;
        return this.currentId;
    }

    // ------------------------------------------------------------------------------------------------------------------------------------
    // --SESSION MANAGEMENT ---------------------------------------------------------------------------------------------------------------
    // ------------------------------------------------------------------------------------------------------------------------------------
    /**
    * updates client activity flags
    **/
    public updateActivity(): void {
        if (this.debugSessionTicks) {
            this.logSession("Client activity detected");
        }

        this.isUserActivityDetected = true;
        this.lastActivity = moment().utc().toDate();
    }

    /**
     * Console logs session information when debugSessionTicks is enabled
     * @param val
     */
    public logSession(val: string): void {
        if (this.debugSessionTicks)
            console.log(moment().utc().format("HH:mm:ss") + "UTC " + val);
    }

    private checkSessionReference: number = -1;

    /**
     * Starts periodic checks for user session inactivity
     * @param tokenExpirationUTC     datetime (utc) of session expiration in token
     */
    public startSessionInactivityCheck(tokenExpirationUTC: Date): void {
        // clear any previous checks
        clearTimeout(this.checkSessionReference);

        let self = this;
        this.tokenExpiration = tokenExpirationUTC;
        this.timeUntilSessionInactiveMinutes = cacheStore.SessionLengthMinutes; // timelapse after which session becomes inactive if no user activity detected
        this.timeUntilSessionSlideMinutes = self.timeUntilSessionInactiveMinutes / 2; // timelapse after which backend slides session expiration window when user activity detected
        this.timeDurationWarningPopupMinutes = 0.5; // time for which we display warning popup that "session will be inactivated if no user activity detected in next X seconds"
        this.timeGracePeriodMinutes = this.timeUntilSessionSlideMinutes - this.timeDurationWarningPopupMinutes;
        this.isUserActivityDetected = false;

        self.logSession("SessionInactivityCheck Started. token expiring @" + moment.utc(this.tokenExpiration).format("HH:mm:ss") + "UTC timeUntilSessionInactiveMinutes= " + self.timeUntilSessionInactiveMinutes + " mins. timeUntilSessionSlideMinutes= " + self.timeUntilSessionSlideMinutes + "mins");

        this.checkSessionReference = window.setTimeout(() => {
            this.checkSessionInactivity(true, false);
        }, this.timeUntilSessionSlideMinutes * 1000 * 60);
    }

    public stopSessionActivityCheck(): void {
        this.logSession("Session Activity Check stopped");
        clearTimeout(this.checkSessionReference);
    }

    /**
     * Checks for user session activity and call appropriate operations depending on last activity time
     * @param isRecursive   is one shot only, or keeps calling itself recursively?
     * @param isGracePeriod displays a warning popup if no user activity detected
     * @param doLogout      does a checkSession backend call to logout user if no user activity detected
     */
    public checkSessionInactivity(isRecursive: boolean = false, isGracePeriod: boolean = false, doLogout: boolean = false): void {
        let self = this;

        self.logSession("Checking Session Inactivity");

        if (self.isUserActivityDetected) {
            self.logSession("User activity detected in last " + self.timeUntilSessionSlideMinutes + "mins @" + moment.utc(this.lastActivity).format("HH:mm:ss") + "UTC. Calling server to slide expiration window.");

            // an user activity has been detected, call server handler so it will slide the expiration in session token
            self.action.checkSession();

            // call check session again if set to recursive
            self.logSession("CheckSessionActivity rescheduled to run in " + self.timeUntilSessionSlideMinutes + "mins");
            self.isUserActivityDetected = false;
            this.checkSessionReference = window.setTimeout(() => {
                self.checkSessionInactivity(isRecursive, false);
            }, self.timeUntilSessionSlideMinutes * 60 * 1000);
        }
        else {
            if (!isGracePeriod && !doLogout) {
                self.logSession("No user activity detected in last session inactivity period (" + self.timeUntilSessionSlideMinutes + " mins) Last activity @" + moment.utc(this.lastActivity).format("HH:mm:ss") + ". Rescheduling checkSessionTimeout with graceperiod=" + this.timeGracePeriodMinutes + " mins");

                this.checkSessionReference = window.setTimeout(() => {
                    self.checkSessionInactivity(isRecursive, true);
                }, this.timeGracePeriodMinutes * 60 * 1000);
            }
            else if (isGracePeriod) {
                self.logSession("No user activity detected during grace period (" + self.timeGracePeriodMinutes + " mins) Last activity @" + moment.utc(this.lastActivity).format("HH:mm:ss") + ". Rescheduling checkSessionTimeout in=" + this.timeDurationWarningPopupMinutes + " mins");

                var operation = new LocalEvent(LocalOperations.MainComponentViewOperations.DisplaySessionWarningPopup);
                MainComponentStateDispatcher.dispatch(operation);

                this.checkSessionReference = window.setTimeout(() => {
                    self.checkSessionInactivity(isRecursive, false, true);
                }, self.timeDurationWarningPopupMinutes * 60 * 1000);
            }
            else if (doLogout) {
                self.logSession("No user activity detected since grace period is over. Logging off.");

                this.action.logout(true);
            }
            else {
                self.logSession("No user activity detected. Case Impossibru!");
            }
        }
    }

    // ------------------------------------------------------------------------------------------------------------------------------------
    // --END SESSION MANAGEMENT ---------------------------------------------------------------------------------------------------------------
    // ------------------------------------------------------------------------------------------------------------------------------------

    public dispatch(operation: number, params: any = {}, delayms: number = null): void {
        this.dispatchRaw(operation, params, delayms);
    }

    public dispatchMain(operation: LocalOperations.MainComponentViewOperations, params: any = {}, delayms: number = null): void {
        this.dispatchRaw(operation, params, delayms, MainComponentStateDispatcher);
    }

    private dispatchRaw(operation: number, params: any = {}, delayms: number = null, dispatcher: Dispatcher<any> = LocalEventDispatcher): void {
        function action(): void {
            let localEvent = new LocalEvent(operation);
            for (let key of Object.keys(params || {}))
                localEvent.AddItem(key, params[key]);

            dispatcher.dispatch(localEvent);
        }

        if (delayms != null)
            window.setTimeout(action, delayms);
        else
            action();
    }

    /**
     * Dispatch an operation asynchronously. This is used in cases where the caller doesn't need to wait for the result, and it may already be in the middle of a dispatch (since dispatching within a dispatch isn't allowed).
     * @param operation The operation ID to dispatch.
     * @param params The parameters which can be read by the operation handlers.
     * @param delayms The delay in milliseconds before the operation is dispatched. This must be at least 1.
     */
    public dispatchAsync(operation: number, params: object = {}, delayms: number = 1): void {
        delayms = Math.max(delayms || 0, 1);
        this.dispatch(operation, params, delayms);
    }

    public setEncounterTimer(model: TimerComponentModel, initValueFromCachedEncounter: boolean = true, emitChange: boolean = true): void {
        if (model != null) {
            model.initialValueResetId = model.patientContactId + "_" + model.encounterId + "_" + model.initialValue.ticks.toString();

            let noteModel = patientStore.PatientMedicalNoteModels[model.patientContactId];
            if (noteModel != null && initValueFromCachedEncounter == true) {
                let encounter = noteModel.get("Encounter");
                if (encounter.Id == model.encounterId) {
                    let employes = encounter.EncounterRelatedEmployees.filter(x => x.EmployeeId == sessionStore.getCurrentEmployee().ContactFacetId);
                    let currentEmp = employes.length > 0 ? employes[0] : null;
                    let initialTime = currentEmp == null || IsNullOrWhiteSpace(currentEmp.DurationTicks) ? 0 : Number(currentEmp.DurationTicks);
                    model.initialValue = TimeSpan.FromTicks(initialTime);
                }
            }
        }
        else
            sessionStore.timerCurrentValue = null;

        sessionStore.timerComponentModel = model;

        if (emitChange)
            sessionStore.EmitChangeToListeners();
    }

    public signOnPrintAndFaxAccess(): boolean {
        return this.signOnPrintAccess && this.faxAccess;
    }

    /**
     * Get whether the current user can sign a prescription.
     * @param prescribedByEmployeeId The contact facet ID for the user who created the prescription.
     */
    public canSignPrescription(prescribedByEmployeeId: string): boolean {
        const currentEmployee = sessionStore.getCurrentEmployee();
        const isSignOnPrintAccessValid = cacheStore.ClinicSettings.EnableQuickDocumentSigning || sessionStore.signOnPrintAccess;

        return (
            isSignOnPrintAccessValid
            && (
                prescribedByEmployeeId === currentEmployee.ContactFacetId
                || EmployeeTitleHelper.isDoctor(sessionStore.myleEmployeeTitle)
                || sessionStore.myleEmployeeTitle === EmployeeTitle.NursePractitioner
            )
        );
    }

    /**
    * Get the locale value equivalent to a numeric locale code from the backend, if valid.
    * @param backendLocale The backend locale code.
     */
    public getLocaleFromBackendCode(backendLocale: LocaleNumber): Locale {
        // get name like "en" from raw value
        const name = isNaN(backendLocale)
            ? `${backendLocale}`
            : LocaleNumber[backendLocale];

        // get equivalent locale value (if any)
        return Locale[name];
    }

    /**
     * Save a temporary value for reuse across multiple forms/screens in the current session. The value is stored in-memory, so it's not persisted between browser tabs or sessions.
     * @param key A globally unique key for the value.
     * @param value The value to cache.
     */
    public setLockData<TData>(key: string, value: TData): void {
        this.lockedData[key] = value;
    }

    /**
     * Get a temporary value saved using setLock, if found.
     * @param key The globally unique key for the saved field.
     */
    public getLockedData<TData>(key: string): TData | null {
        return this.lockedData[key] ?? null;
    }

    /**
     * Discard a temporary value saved using setLock.
     * @param key The globally unique key for the saved field.
     */
    public clearLockedData(key: string): void {
        delete this.lockedData[key];
    }

    /**
     * Get whether a temporary value was saved using setLock.
     * @param key The globally unique key for the saved field.
     */
    public isLockedData(key: string): boolean {
        return this.lockedData[key] !== undefined;
    }

    /**
     * Get the source identifier when searching medication info in the backend.
     */
    public getMedicationSourceKey(): string {
        if (this.getCurrentEmployee().isActiveSiteMexico() || this.getCurrentEmployee().isActiveSiteColombia())
            return "PLM";

        return "DEFAULT";
    }

    public savePharmaNetCredentials(username: string, password: string): void {
        if (!username || !password)
            return;

        this.pharmaNetUsername = username;
        this.pharmaNetPassword = password;
    }

    public hasPharmaNetCredentials(): boolean {
        return !!this.pharmaNetUsername && !!this.pharmaNetPassword;
    }

    public getCurrentEmployeeLicenseDto(): LicenseDto {
        return this.getCurrentEmployee().getLicenseDto();
    }

    /**
     * Indicates whether or not CareConnect is active for the active site (British Columbia sites only)
     */
    public isBcCareConnectActive(): boolean {
        return cacheStore.ClinicSettings.EnableBcCareConnect && this.getCurrentEmployee().isActiveSiteBC();
    }

    /**
     * Indicates whether the current user can receive Inbound Faxes.
     */
    public canReceiveFaxes(): boolean {
        return cacheStore.canReceiveFaxes()
            && sessionStore.hasPermission(EnumMyleLoginPermission.InboundFaxPermission);
    }

    public completeWorkflowStep(stepEnum: LoginWorkflowStepEnum, onBeforeCompleteMutation?: (store: SessionStore) => void): void {
        onBeforeCompleteMutation?.(this);
        this.loginWorkflow.completeStep(stepEnum);
        this.EmitChangeToListeners();
    }

    public cancelWorkflowStep(stepEnum: LoginWorkflowStepEnum): void {
        this.loginWorkflow.cancelStep(stepEnum);
        this.EmitChangeToListeners();
    }

    public validatePrescriptionRenewalBeforeDocumentSave(prescriptionRenewalResponse: PrescriptionRenewalItemResponse, documentClassId: string, saveDocument: () => void): void {
        const prescriptionRenewal = PrescriptionRenewal.fromResponseModel(prescriptionRenewalResponse);

        if (!prescriptionRenewal || documentClassId === cacheStore.FindLookupByLookupCode(DocumentClass.MedicationAndPrescriptions).LookupID) {
            saveDocument();
            return;
        }

        // If the document has a completed prescription renewal, it will not be possible to modify the document class
        if (prescriptionRenewal.status === PrescriptionRenewalStatus.Completed) {
            this.showPopup({
                title: cacheStore.GetString("WarningLabel"),
                content: cacheStore.GetString("WarningEditDocumentClassWithPrescriptionRenewalCompleted")
            });
            return;
        }

        this.showPopup({
            title: cacheStore.GetString("WarningLabel"),
            content: [cacheStore.GetString("WarningEditDocumentClassWithPrescriptionRenewal"), cacheStore.GetString("DoYouWantToContinueLabel")],
            showCancel: true,
            okAction: () => {
                saveDocument();
                fetchPrescriptionsRenewalCounters();
            }
        });
    }

    public hasPermissionsToPrescribe(): boolean {
        const employee = this.getCurrentEmployee();
        const title = employee.getIssuingOrgRule().Title;

        return employee.Type === EmployeeType.Practician
            && this.hasRightsForAction([EmployeeType.Practician])
            && this.userPermissions.includes("DocumentTabPermission")
            && this.userPermissions.includes("PrescriptionRenewalTabPermission")
            && this.userPermissions.includes("PatientTabPermission")
            && this.isIntervenor(title);
    }

    public hasPermissionsToSeePrescribeActions(): boolean {
        const employee = this.getCurrentEmployee();

        return (
            employee.Type !== EmployeeType.Assistant
            && employee.getIssuingOrgRule().Title !== EmployeeTitle.MedicalOfficeAssistant
        );
    }

    public hasPermissionToApproveMedicalDocument(): boolean {
        const title = sessionStore.getCurrentEmployee().getIssuingOrgRule().Title;
        return this.hasRightsForAction([EmployeeType.Practician])
            && this.userPermissions.includes("DocumentTabPermission")
            && this.isIntervenor(title);
    }

    /**
     * Get whether the title is a resident, nurse practitioner, doctor, nurse, or professional.
     * @param title The employee title to check.
     */
    public isIntervenor(title: EmployeeTitle): boolean {
        return (
            EmployeeTitleHelper.isDoctor(title)
            || title === EmployeeTitle.Resident
            || EmployeeTitleHelper.isNurse(title)
            || EmployeeTitleHelper.isProfessional(title)
        );
    }

    public async OpenExternalSearchPopup(patientModel: Patient): Promise<void> {
        sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.DisplayExternalSearch, {
            identifier: PatientExternalIdentifier.GetExternalSearchDefaultSearchForActiveSite(patientModel),
            patientContactId: patientModel.IndividualResponse.Contact_id,
            callback: () => patientStore.ActionsProfile.loadPatient(patientModel.IndividualResponse.Contact_id)
        });
    }

    public initializeCallCenterNotificationHandler(): void {
        // cannot initialize in constructor as signalR has not yet connected
        if (this.callCenterNotificationHandler == null) {
            this.callCenterNotificationHandler = new CallCenterRealTimeNotificationHandler(this);
        }
    }

    public updateCallCenterUserState(updatedCallCenterUserState: CallCenterUserState): void {
        this.callCenterUserState = updatedCallCenterUserState;
    }

    public hasCrdsAccess(): boolean {
        return !IsNullOrWhiteSpace(this.getCurrentEmployee().getActiveSite().Model.ClinicHistoricalIds);
    }
}

export { SessionStore, defaultInstance as sessionStore };
